import type {
  PatchAdminV1StoresStoreIdStockLocationsIdBody,
  PostAdminV1StoresStoreIdInventoryAdjustBody,
  PostAdminV1StoresStoreIdStockLocationsBody,
  StockLocation,
} from '@nucleus-commerce/api-client/admin';

import type { AdjustStockFormInput, AdjustStockFormOutput } from '../schemas/adjust-stock-schema';
import type { LocationFormInput, LocationFormOutput } from '../schemas/location-form-schema';

export function emptyAdjustForm(): AdjustStockFormInput {
  return { kind: 'receive', quantity: '', reason: '' };
}

export function toAdjustBody(
  values: AdjustStockFormOutput,
  variantId: string,
  locationId: string,
): PostAdminV1StoresStoreIdInventoryAdjustBody {
  return {
    variantId,
    locationId,
    kind: values.kind,
    quantity: values.quantity,
    reason: values.reason,
  };
}

export function emptyLocationForm(): LocationFormInput {
  return {
    name: '',
    line1: '',
    line2: '',
    city: '',
    region: '',
    postalCode: '',
    countryCode: '',
    fulfillsOnlineOrders: true,
    isDefault: false,
  };
}

export function fromLocation(location: StockLocation): LocationFormInput {
  return {
    name: location.name,
    line1: location.address?.line1 ?? '',
    line2: location.address?.line2 ?? '',
    city: location.address?.city ?? '',
    region: location.address?.region ?? '',
    postalCode: location.address?.postalCode ?? '',
    countryCode: location.address?.countryCode ?? '',
    fulfillsOnlineOrders: location.fulfillsOnlineOrders,
    isDefault: location.isDefault,
  };
}

const orNull = (value: string) => (value === '' ? null : value);

/** A form with every address line blank sends `address: null`, which clears a stored one. */
function toAddress(values: LocationFormOutput) {
  if (values.line1 === '' && values.city === '' && values.countryCode === '') return null;
  return {
    line1: values.line1,
    line2: orNull(values.line2),
    city: values.city,
    region: orNull(values.region),
    postalCode: orNull(values.postalCode),
    countryCode: values.countryCode,
  };
}

export function toLocationCreate(
  values: LocationFormOutput,
): PostAdminV1StoresStoreIdStockLocationsBody {
  return {
    name: values.name,
    address: toAddress(values),
    fulfillsOnlineOrders: values.fulfillsOnlineOrders || values.isDefault,
    ...(values.isDefault ? { isDefault: true } : {}),
  };
}

/**
 * The API only takes `isDefault: true`; the flag moves away from a location by giving it to
 * another. The default always fulfils online orders (D-175), so the switch follows the flag.
 */
export function toLocationUpdate(
  values: LocationFormOutput,
  current: Pick<StockLocation, 'isDefault'>,
): PatchAdminV1StoresStoreIdStockLocationsIdBody {
  return {
    name: values.name,
    address: toAddress(values),
    fulfillsOnlineOrders: values.fulfillsOnlineOrders || values.isDefault,
    ...(values.isDefault && !current.isDefault ? { isDefault: true as const } : {}),
  };
}
